import React, { useState, useCallback, useEffect } from 'react';
import { useQuizWebSocket } from '../hooks/useQuizWebSocket';
import { resumeAudioContext } from '../utils/soundEffects';
import QuizLobby from '../modes/quiz-battle/QuizLobby';
import QuizGame from '../modes/quiz-battle/QuizGame';
import QuizResults from '../modes/quiz-battle/QuizResults';

/**
 * StudentJoinPage — Inkwell editorial join for unauthenticated students.
 * Mobile-first centered card with vermillion 学 hero, mono room-code input.
 */
function StudentJoinPage({ initialRoomCode }) {
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState(initialRoomCode || '');
  const [joinError, setJoinError] = useState('');

  const ws = useQuizWebSocket();

  useEffect(() => {
    const resume = () => {
      resumeAudioContext();
      window.removeEventListener('click', resume);
    };
    window.addEventListener('click', resume);
    return () => window.removeEventListener('click', resume);
  }, []);

  const handleJoin = useCallback(
    (e) => {
      e.preventDefault();
      const name = playerName.trim();
      const code = roomCode.trim().toUpperCase();
      if (!name) {
        setJoinError('Please enter your name.');
        return;
      }
      if (!code || code.length < 4) {
        setJoinError('Please enter a valid room code.');
        return;
      }
      setJoinError('');
      ws.joinRoom(name, code);
    },
    [playerName, roomCode, ws]
  );

  const handleBackToJoin = useCallback(() => {
    ws.disconnect();
    const targetPath = initialRoomCode ? `/join/${initialRoomCode}` : '/join';
    window.history.replaceState({}, '', targetPath);
  }, [ws, initialRoomCode]);

  // Lobby phase
  if (ws.phase === 'lobby') {
    return (
      <QuizLobby
        phase={ws.phase}
        roomCode={ws.roomCode}
        players={ws.players}
        isHost={ws.isHost}
        error={ws.error}
        onCreateRoom={() => {}}
        onJoinRoom={() => {}}
        onStartGame={ws.startGame}
        onLeave={handleBackToJoin}
      />
    );
  }

  // Finished phase
  if (ws.phase === 'finished') {
    return (
      <QuizResults
        finalScores={ws.finalScores}
        playerId={ws.playerId}
        onPlayAgain={ws.rematch}
        onBackToMenu={handleBackToJoin}
      />
    );
  }

  // Playing/countdown phase
  if (ws.phase === 'countdown' || ws.phase === 'playing') {
    return (
      <QuizGame
        phase={ws.phase}
        gameType={ws.gameType}
        questions={ws.questions}
        currentIndex={ws.currentIndex}
        timeLimit={ws.timeLimit}
        countdown={ws.countdown}
        lastResult={ws.lastResult}
        totalCorrect={ws.totalCorrect}
        penaltyMs={ws.penaltyMs}
        playerProgress={ws.playerProgress}
        playerId={ws.playerId}
        onSubmitAnswer={ws.submitAnswer}
        onAdvance={ws.advanceQuestion}
      />
    );
  }

  // Idle: join form
  const hasRoomCodeFromUrl = Boolean(initialRoomCode);
  const canSubmit = playerName.trim().length > 0 && roomCode.trim().length >= 4;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--card)',
          border: '1px solid var(--rule)',
          borderRadius: 12,
          padding: '40px 32px 32px',
          boxShadow: 'var(--sh3)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              fontFamily: 'var(--font-han)',
              fontSize: 144,
              lineHeight: 1,
              color: 'var(--vermillion)',
              marginBottom: 24,
              fontWeight: 500,
              letterSpacing: '0.04em',
            }}
          >
            学
          </div>
          <div className="t-eyebrow" style={{ marginBottom: 8 }}>
            入场 · Join
          </div>
          <h1 className="t-h1" style={{ fontSize: 28, marginBottom: 8 }}>
            Join quiz
          </h1>
          <p className="t-small">Enter your name to join the battle.</p>
        </div>

        <form onSubmit={handleJoin}>
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label">Your name</label>
            <input
              className="input input--lg"
              placeholder="Type your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
              autoFocus
            />
          </div>

          <div className="field" style={{ marginBottom: 24 }}>
            <label className="field-label">Room code</label>
            <input
              className="input input--lg input--mono"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase().slice(0, 4))}
              style={{ textAlign: 'center', fontSize: 24 }}
              maxLength={4}
              readOnly={hasRoomCodeFromUrl}
              placeholder="ABCD"
            />
          </div>

          {joinError && (
            <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 16 }}>
              {joinError}
            </p>
          )}
          {ws.error && (
            <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 16 }}>
              {ws.error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn--accent btn--lg btn--block"
            disabled={!canSubmit}
          >
            Join game →
          </button>
        </form>

        <p className="t-small" style={{ textAlign: 'center', marginTop: 16 }}>
          No account needed · works on any phone
        </p>
      </div>
    </div>
  );
}

export default StudentJoinPage;
